import { useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import toast from "react-hot-toast";
import { useVoidExpenseMutation } from "@/redux/services/expense/expense.api";
import { Expense } from "@/redux/services/expense/expense.type";
import { getExpenseVoidMessage } from "@/lib/expenses";
import { toastApiError } from "@/lib/apiErrors";

interface VoidExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense: Expense;
}

const VoidExpenseModal = ({
  isOpen,
  onClose,
  expense,
}: VoidExpenseModalProps) => {
  const [reason, setReason] = useState("");
  const [voidExpense, { isLoading }] = useVoidExpenseMutation();

  const trimmedReason = reason.trim();

  const handleVoid = async () => {
    if (!trimmedReason) return;
    const res = await voidExpense({
      expenseUuid: expense.id,
      voidReason: trimmedReason,
      drawerBacked: expense.paidFromDrawer,
    });

    if ("data" in res) {
      // Closed-session void: the API returns a refund-reminder message key
      // instead of posting a compensating cash movement. Surface it so the
      // operator knows to record a manual cash-in if the money came back.
      const notice = getExpenseVoidMessage(res.data?.voidNotice);
      if (notice) {
        toast.success(notice, { duration: 8000 });
      } else {
        toast.success("Dépense annulée");
      }
      setReason("");
      onClose();
    } else if ("error" in res) {
      // The API returns message keys in `detail` (e.g. the manager-only 403 on
      // a closed session); the shared helper maps them to French.
      toastApiError(res.error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Annuler la dépense" size="medium">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          L&apos;annulation conserve l&apos;historique : la dépense reste visible
          avec le statut « Annulée ». Cette action est définitive.
        </p>
        <div>
          <label
            htmlFor="void-reason"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Motif <span className="text-destructive">*</span>
          </label>
          <textarea
            id="void-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Ex: erreur de saisie"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Le motif est conservé dans l&apos;historique de la dépense.
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Retour
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            isLoading={isLoading}
            disabled={!trimmedReason}
            onClick={handleVoid}
          >
            Annuler la dépense
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default VoidExpenseModal;
